import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Optional,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
} from '@angular/forms';

import {TuiDay, TuiMonth} from '@taiga-ui/cdk';
import {TuiDropdown, TuiTextfield} from '@taiga-ui/core';
import {TuiCalendarMonth, TuiInputDate, TuiInputMonth} from '@taiga-ui/kit';

import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';

type DateInput = TuiDay | TuiMonth | string | Date | null;

@Component({
  standalone: true,
  selector: 'gatewee-datepicker-v2',
  imports: [
    CommonModule,
    FormsModule,
    TuiInputDate,
    TuiInputMonth,
    TuiCalendarMonth,
    TuiTextfield,
    TuiDropdown,
    ErrorMessageComponent,
  ],
  templateUrl: './gatewee-datepicker-v2.component.html',
  styleUrls: ['./gatewee-datepicker-v2.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GateweeDatepickerV2Component implements ControlValueAccessor {
  @Input() label = 'Selecione uma data';
  /** When set, used as native input `id` / `name` (and label `for`). Overrides `NgControl.name`-based id. */
  @Input() inputId = '';
  @Input() mode: 'date' | 'month' = 'date';
  @Input() minDate: TuiDay | null = null;
  @Input() maxDate: TuiDay | null = null;
  @Input() required = false;
  @Input() placeholder = 'dd/mm/aaaa';

  value: TuiDay | null = null;
  monthValue: TuiMonth | null = null;
  disabled = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control(): FormControl | null {
    return this.ngControl?.control as FormControl;
  }

  get effectiveInputId(): string {
    const explicit = this.inputId?.trim();
    if (explicit) {
      return explicit;
    }
    const n = this.ngControl?.name;
    if (n !== null && n !== undefined && String(n).trim() !== '') {
      return `gatewee-datepicker-${String(n)}`;
    }
    return 'gatewee-datepicker';
  }

  get minMonth(): TuiMonth | null {
    return this.minDate
      ? new TuiMonth(this.minDate.year, this.minDate.month)
      : null;
  }

  get maxMonth(): TuiMonth | null {
    return this.maxDate
      ? new TuiMonth(this.maxDate.year, this.maxDate.month)
      : null;
  }

  writeValue(value: DateInput): void {
    if (this.mode === 'month') {
      this.monthValue = this.normalizeToTuiMonth(value);
      return;
    }

    this.value = this.normalizeToTuiDay(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleChange(value: TuiDay | null): void {
    this.value = value;

    const isoValue = value ? value.getFormattedDay('YMD', '-') : null;

    this.onChange(isoValue);
    this.onTouched();
  }

  handleMonthChange(value: TuiMonth | null): void {
    this.monthValue = value;

    const isoValue = value
      ? `${value.year}-${String(value.month + 1).padStart(2, '0')}-01`
      : null;

    this.onChange(isoValue);
    this.onTouched();
  }

  markTouched(): void {
    this.onTouched();
  }

  private parseLocalDateFromYmd(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      return new Date(+match[1], +match[2] - 1, +match[3]);
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  private normalizeToTuiDay(value: DateInput): TuiDay | null {
    if (!value) return null;

    if (value instanceof TuiDay) {
      return value;
    }

    if (value instanceof TuiMonth) {
      return new TuiDay(value.year, value.month, 1);
    }

    if (value instanceof Date) {
      return TuiDay.fromUtcNativeDate(value);
    }

    if (typeof value === 'string') {
      const date = this.parseLocalDateFromYmd(value);
      return date ? TuiDay.fromLocalNativeDate(date) : null;
    }

    return null;
  }

  private normalizeToTuiMonth(value: DateInput): TuiMonth | null {
    if (!value) return null;

    if (value instanceof TuiMonth) {
      return value;
    }

    if (value instanceof TuiDay) {
      return new TuiMonth(value.year, value.month);
    }

    if (value instanceof Date) {
      return new TuiMonth(value.getFullYear(), value.getMonth());
    }

    if (typeof value === 'string') {
      const date = this.parseLocalDateFromYmd(value);
      return date ? new TuiMonth(date.getFullYear(), date.getMonth()) : null;
    }

    return null;
  }
}
